/**
 * Client-side JavaScript: the Elements pane.
 *
 * Lists the elements that differ between the two renders, and draws a box over
 * the screenshot for whichever one the reader is looking at. This is what turns
 * "80,858 pixels changed" into "this card gained a 24px margin".
 *
 * Boxes are positioned as percentages of the comparison's capture box, which is
 * how the platform's existing region overlay does it — no measuring, no resize
 * handling, correct at any device pixel ratio or rendered size.
 *
 * ES5-compatible vanilla JS, concatenated into the report's single inline
 * script under a CSP that permits no external resources.
 */

export const JS_DOM = `
  /* ── DOM comparison ── */

  /* Rank-cycled so a row and its box always share a colour. */
  var DOM_TONES = ['#03b79c','#f59e0b','#60a5fa','#e879f9','#ef4444','#a3e635'];
  function domTone(rank){return DOM_TONES[(rank-1)%DOM_TONES.length];}

  function domKindLabel(kind){
    if(kind==='restyled')return 'restyled';
    if(kind==='text')return 'text';
    if(kind==='resized')return 'resized';
    return kind;
  }

  /* Percentages of the capture box: the image may be rendered at any size. */
  function domBoxStyle(rect,box){
    if(!rect||!box||!box[2]||!box[3])return null;
    /* A degenerate rect draws a hairline that points at nothing. The platform
       and Studio drop it; this copy did not, which is exactly the kind of
       quiet divergence the shared vectors exist to catch. */
    if(rect[2]<=0||rect[3]<=0)return null;
    var left=((rect[0]-box[0])/box[2])*100;
    var top=((rect[1]-box[1])/box[3])*100;
    var width=(rect[2]/box[2])*100;
    var height=(rect[3]/box[3])*100;
    /* An element scrolled out of the captured region has nothing to point at. */
    if(left>100||top>100||left+width<0||top+height<0)return null;
    return 'left:'+left.toFixed(3)+'%;top:'+top.toFixed(3)+'%;width:'+width.toFixed(3)+'%;height:'+height.toFixed(3)+'%';
  }

  function renderDomOverlay(v){
    var h='<div class="dom-ov">';
    for(var i=0;i<v.domChanges.length;i++){
      var c=v.domChanges[i];
      /* A removed element has no box in this render; show where it used to be. */
      var style=domBoxStyle(c.rect||c.baselineRect,v.domCaptureBox);
      if(!style)continue;
      h+='<div class="dom-box" data-dom-box="'+c.rank+'" style="'+style+';border-color:'+domTone(c.rank)+'"></div>';
    }
    return h+'</div>';
  }

  function renderDomRow(c){
    var h='<div class="dom-row" data-dom-row="'+c.rank+'">';
    h+='<span class="dom-swatch" style="background:'+domTone(c.rank)+'"></span>';
    h+='<span class="dom-label">'+esc(c.label)+'</span>';
    for(var k=0;k<c.kinds.length;k++){
      h+='<span class="dom-kind dk-'+esc(c.kinds[k])+'">'+esc(domKindLabel(c.kinds[k]))+'</span>';
    }
    h+='<span class="dom-area">'+(c.areaPct*100).toFixed(1)+'%</span>';
    h+='</div>';

    var detail='';
    for(var a=0;a<(c.attrs||[]).length;a++){
      var at=c.attrs[a];
      detail+='<div class="dom-prop dom-prop-attr"><span class="dom-prop-name">'+esc(at.prop)+'</span>'+
        '<span class="dom-from">'+esc(at.from)+'</span>'+
        '<span class="dom-arrow">&rarr;</span>'+
        '<span class="dom-to">'+esc(at.to)+'</span></div>';
    }
    for(var s=0;s<(c.styles||[]).length;s++){
      var st=c.styles[s];
      detail+='<div class="dom-prop"><span class="dom-prop-name">'+esc(st.prop)+'</span>'+
        '<span class="dom-from">'+esc(st.from)+'</span>'+
        '<span class="dom-arrow">&rarr;</span>'+
        '<span class="dom-to">'+esc(st.to)+'</span></div>';
    }
    if(c.text){
      detail+='<div class="dom-prop"><span class="dom-prop-name">text</span>'+
        '<span class="dom-from">'+esc(c.text.from)+'</span>'+
        '<span class="dom-arrow">&rarr;</span>'+
        '<span class="dom-to">'+esc(c.text.to)+'</span></div>';
    }
    if(c.baselineRect&&c.rect){
      var moved=(c.baselineRect[0]!==c.rect[0]||c.baselineRect[1]!==c.rect[1]);
      var sized=(c.baselineRect[2]!==c.rect[2]||c.baselineRect[3]!==c.rect[3]);
      if(moved||sized){
        detail+='<div class="dom-prop"><span class="dom-prop-name">rect</span>'+
          '<span class="dom-from">'+c.baselineRect.join(', ')+'</span>'+
          '<span class="dom-arrow">&rarr;</span>'+
          '<span class="dom-to">'+c.rect.join(', ')+'</span></div>';
      }
    }
    if(detail)h+='<div class="dom-detail">'+detail+'</div>';
    return h;
  }

  function renderDomPane(v){
    var changes=v.domChanges||[];
    if(changes.length===0){
      return '<div class="vis-note">The DOM matched its baseline exactly \\u2014 every element in the same place, with the same styles and text.</div>';
    }

    var roots=[],shifted=[];
    for(var i=0;i<changes.length;i++){
      if(changes[i].consequence)shifted.push(changes[i]);else roots.push(changes[i]);
    }

    var h='';
    var image=v.actual||v.baseline;
    if(image){
      h+='<div class="dom-figure">';
      h+='<img class="dom-img" src="'+esc(image)+'" alt="Changed elements">';
      h+=renderDomOverlay(v);
      h+='</div>';
    }

    h+='<div class="dom-summary">';
    h+='<strong>'+(v.domChangeCount||changes.length)+'</strong> element'+((v.domChangeCount||changes.length)===1?'':'s')+' differ';
    if(shifted.length>0){
      /* Say what we actually know: these only moved. Not that the one above
         moved them — the diff does not prove causation and must not imply it. */
      h+=' \\u00b7 <strong>'+roots.length+'</strong> changed, <strong>'+shifted.length+'</strong> only shifted position';
    }
    if(v.domChangeCount&&v.domChangeCount>changes.length){
      h+=' \\u00b7 showing the top '+changes.length;
    }
    if(v.domTruncated)h+=' <span class="dom-truncated">capture truncated</span>';
    h+='</div>';

    h+='<div class="dom-list">';
    for(var r=0;r<roots.length;r++)h+=renderDomRow(roots[r]);
    if(shifted.length>0){
      h+='<div class="dom-group-label">Moved or resized only</div>';
      for(var m=0;m<shifted.length;m++)h+=renderDomRow(shifted[m]);
    }
    h+='</div>';
    return h;
  }

  /* Hover a row, highlight its box. Delegated so it covers cards that are
     rendered into the drawer long after this runs. */
  function domSetActive(card,rank,on){
    var box=card.querySelector('[data-dom-box="'+rank+'"]');
    if(box)box.classList.toggle('hi',on);
    var row=card.querySelector('[data-dom-row="'+rank+'"]');
    if(row)row.classList.toggle('hi',on);
  }

  document.addEventListener('mouseover',function(e){
    var row=e.target.closest?e.target.closest('.dom-row'):null;
    if(!row)return;
    var card=row.closest('.vis-card');
    if(card)domSetActive(card,row.getAttribute('data-dom-row'),true);
  });

  document.addEventListener('mouseout',function(e){
    var row=e.target.closest?e.target.closest('.dom-row'):null;
    if(!row)return;
    var card=row.closest('.vis-card');
    if(card&&!row.classList.contains('pinned'))domSetActive(card,row.getAttribute('data-dom-row'),false);
  });
`;
